import React, { HTMLAttributes } from "react";
import { useTheme } from "../theme/ThemeProvider";

export type CardProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  media?: React.ReactNode;
  actions?: React.ReactNode;
  footer?: React.ReactNode;
  elevation?: "none" | "sm" | "md" | "lg";
  hoverable?: boolean;
  noPadding?: boolean;
};

export const Card: React.FC<CardProps> = ({
  children,
  title,
  subtitle,
  media,
  actions,
  footer,
  elevation = "sm",
  hoverable = false,
  noPadding = false,
  className = "",
  ...props
}) => {
  const theme = useTheme();

  const elevationStyles = {
    none: "",
    sm: "shadow-sm",
    md: "shadow-md",
    lg: "shadow-lg",
  };

  return (
    <div
      className={`py-4 
        bg-white rounded-lg overflow-hidden
        ${elevationStyles[elevation]}
        ${hoverable ? "transition-shadow hover:shadow-lg" : ""}
        ${className}
      `}
      style={{
        borderColor: theme.colors.primary + "20",
        borderWidth: "1px",
      }}
      {...props}
    >
      {media && <div className="relative">{media}</div>}

      {(title || subtitle) && (
        <div className={`${noPadding ? "" : "px-4 xl:px-6 py-4"}`}>
          {title && (
            <h3
              className="text-base lg:text-lg font-semibold mb-1"
              style={{ color: theme.colors.text }}
            >
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="text-sm" style={{ color: theme.colors.secondary }}>
              {subtitle}
            </p>
          )}
        </div>
      )}

      <div className={`${noPadding ? "" : "px-4 xl:px-6 py-4"}`}>
        {children}
      </div>

      {actions && (
        <div
          className={`
            flex items-center justify-end gap-2
            ${noPadding ? "" : " px-4 xl:px-6 py-4"}
            border-t
          `}
          style={{ borderColor: theme.colors.primary + "10" }}
        >
          {actions}
        </div>
      )}

      {footer && (
        <div
          className={`
            ${noPadding ? "" : "px-4 xl:px-6 py-4"}
            border-t
          `}
          style={{
            borderColor: theme.colors.primary + "10",
            backgroundColor: theme.colors.background + "05",
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
};

export default Card;
